import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import { StepCompProps, StepCompRef } from "@layouts/stepper";
import { ProductsShortlistValidationStatus } from "@models/platform-analysis/enums/status";
import { AnalysisStep } from "@models/platform-analysis/enums/step";
import { HasUndecidedPlatformDialog } from "@pages/platform-analysis-v2/_id/components/dialogs/has-undecided-product";
import { NoProductShortlistedDialog } from "@pages/platform-analysis-v2/_id/components/dialogs/no-shortlisted-product";
import { ShortlistedProductsExceededLimitDialog } from "@pages/platform-analysis-v2/_id/components/dialogs/shortlisted-exceeded-limit";
import { AnalysisVirtualizedTable } from "@pages/platform-analysis-v2/_id/components/virtualized-table";
import { AnalysisVirtualizedTableStepConfigs } from "@pages/platform-analysis-v2/_id/configs";
import {
  checkStepIsLastVisibleStep,
  validateReviewSelectedProductsStatuses,
} from "@pages/platform-analysis-v2/_id/utils";
import { RootState } from "@redux/store";
import {
  ForwardRefRenderFunction,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { useToggle } from "react-use";
import { BusinessMetricAnalysisSkeleton } from "../../../components/skeletons/business-metric-analysis";

const TABLE_VIRTUALIZE_PROPS =
  AnalysisVirtualizedTableStepConfigs[AnalysisStep.BusinessMetric];

export const BusinessMetricAnalysisStep: ForwardRefRenderFunction<
  StepCompRef,
  StepCompProps
> = ({ stepNumber }, ref) => {
  const { analysisId } = useParams<{ analysisId: string }>();
  const {
    platformAnalysis,
    isCompleted,
    isReadOnly,
    loadPlatformAnalysis,
    disqualifyAllPlatforms,
    updateFinalJSONs,
  } = usePlatformAnalysis();
  const storedIsLoading = useSelector(
    (state: RootState) => state.platformAnalysis.ui.isLoading,
  );
  const storedSteps = useSelector(
    (state: RootState) => state.platformAnalysis?.ui?.steps,
  );

  // #region States
  const [isLoading, toggleLoading] = useToggle(true);
  // #endregion States
  const [showNoShortlistedDialog, toggleShowNoShortlistedDialog] =
    useToggle(false);
  const [isSubmittingDisqualifyDialog, toggleSubmittingDisqualifyDialog] =
    useToggle(false);
  const [
    showShortlistedExceededLimitDialog,
    toggleShowShortlistedExceededLimitDialog,
  ] = useToggle(false);
  const [disqualifyAllDialog, setDisqualifyAllDialog] = useState<
    | {
        callback: (value: boolean | Promise<boolean>) => void;
        numOfShortlistedProducts: number;
        numOfUndecidedProducts: number;
        numOfDisqualifiedProducts: number;
      }
    | undefined
  >(undefined);

  useImperativeHandle(ref, () => ({
    onChangeStep: async (_, isNext) => {
      if (!isNext) return true;
      return await handleOnSubmit();
    },
    onChangeOtherStep: () => Promise.resolve(true),
    onSubmit: handleOnSubmit,
  }));

  const getDisqualifyAllPlatform = async (
    numOfShortlistedProducts: number,
    numOfUndecidedProducts: number,
    numOfDisqualifiedProducts: number,
  ): Promise<boolean> => {
    return await new Promise((res) => {
      setDisqualifyAllDialog({
        callback: res,
        numOfShortlistedProducts,
        numOfUndecidedProducts,
        numOfDisqualifiedProducts,
      });
    });
  };

  const handleOnDisqualifyAllPlatforms = async () => {
    if (!analysisId) return false;
    const isSuccess = await disqualifyAllPlatforms({
      analysisId,
      analysisStep: AnalysisStep.BusinessMetric,
    });
    return !!isSuccess;
  };

  const validateAndHandleReviewProducts = async (
    isLastVisibleStep: boolean,
  ): Promise<boolean> => {
    const selectedProducts =
      platformAnalysis?.businessMetric?.all?.selectedProducts || [];
    const { status, numOfShortlisted, numOfDisqualified, numOfUndecided } =
      validateReviewSelectedProductsStatuses({
        selectedProducts,
        isLastVisibleStep,
      });

    switch (status) {
      case ProductsShortlistValidationStatus.NoShortlisted:
        toggleShowNoShortlistedDialog(true);
        return false;

      case ProductsShortlistValidationStatus.HasUndecided:
        return await getDisqualifyAllPlatform(
          numOfShortlisted,
          numOfUndecided,
          numOfDisqualified,
        );

      case ProductsShortlistValidationStatus.ShortlistLimitExceeded:
        toggleShowShortlistedExceededLimitDialog(true);
        return false;

      default:
        return true;
    }
  };

  const handleOnSubmit = async () => {
    if (!analysisId || analysisId === "new") return false;
    if (isCompleted || isReadOnly) return true;

    const isLastVisibleStep = checkStepIsLastVisibleStep({
      analysisSteps: storedSteps,
      stepToCheck: AnalysisStep.BusinessMetric,
    });

    // Proceed if not in review mode
    if (!platformAnalysis?.isReview) {
      if (isLastVisibleStep) await updateFinalJSONs(analysisId);
      return true;
    }

    // Validate review
    const isValid = await validateAndHandleReviewProducts(isLastVisibleStep);
    if (isValid && isLastVisibleStep) await updateFinalJSONs(analysisId);
    return isValid;
  };

  useEffect(() => {
    if (!analysisId) return;
    loadPlatformAnalysis({
      platformAnalysisId: analysisId,
      stepId: AnalysisStep.BusinessMetric,
    }).then((response) => {
      toggleLoading(!response);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisId]);

  if (isLoading || storedIsLoading || !TABLE_VIRTUALIZE_PROPS)
    return <BusinessMetricAnalysisSkeleton />;

  return (
    <>
      <AnalysisVirtualizedTable
        disabled={isReadOnly || isCompleted}
        stepNumber={stepNumber ?? 0}
        virtualizedProps={TABLE_VIRTUALIZE_PROPS}
      />

      <NoProductShortlistedDialog
        open={showNoShortlistedDialog}
        onClose={() => toggleShowNoShortlistedDialog(false)}
      />

      <ShortlistedProductsExceededLimitDialog
        open={showShortlistedExceededLimitDialog}
        onClose={() => toggleShowShortlistedExceededLimitDialog(false)}
      />

      <HasUndecidedPlatformDialog
        disqualifyProps={disqualifyAllDialog}
        isSubmitting={isSubmittingDisqualifyDialog}
        onClose={() => {
          disqualifyAllDialog?.callback(false);
          setDisqualifyAllDialog(undefined);
        }}
        onSubmit={async () => {
          toggleSubmittingDisqualifyDialog(true);
          handleOnDisqualifyAllPlatforms().then((res) => {
            disqualifyAllDialog?.callback(res);
            toggleSubmittingDisqualifyDialog(false);

            if (!res) return;
            setDisqualifyAllDialog(undefined);
          });
        }}
      />
    </>
  );
};
